import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import CreateProjectModal from './CreateProjectModal';
import ImportExcelModal from './ImportExcelModal';
import ProjectEditModal from './ProjectEditModal';
import DuplicateAnalysis from './DuplicateAnalysis';
import RoleGuard from './RoleGuard';
import UserMenu from './UserMenu';
import NavigationBar from './NavigationBar';
import { useSystemSettings } from '../hooks/useSystemSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsList = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { industries: systemIndustries, stages: systemStages, getStageLabel } = useSystemSettings();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDuplicateAnalysis, setShowDuplicateAnalysis] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [managers, setManagers] = useState([]);
  const [assigningManager, setAssigningManager] = useState(null);

  // Drag-to-scroll state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    industry: '',
    stages: [], // Changed from stage to stages array for multiple selection
    flag: '',
    manager: '',
    budgetMin: '',
    budgetMax: ''
  });
  
  const [showLostProjects, setShowLostProjects] = useState(false); // Hide lost projects by default
  
  const [sortBy, setSortBy] = useState('deadline'); // Default sort by deadline (quarter + year)
  const [sortOrder, setSortOrder] = useState('asc'); // asc = по возрастанию (от раннего к позднему)

  // Drag-to-scroll handlers
  const handleMouseDown = (e) => {
    const container = document.getElementById('table-container');
    if (!container) return;
    
    // Don't start dragging if clicking on interactive elements
    if (e.target.closest('input') || 
        e.target.closest('textarea') || 
        e.target.closest('button') || 
        e.target.closest('a') || 
        e.target.closest('select')) {
      return;
    }
    
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
    container.style.cursor = 'grabbing';
    container.style.userSelect = 'none';
  };

  const handleMouseLeave = () => {
    const container = document.getElementById('table-container');
    if (container) {
      container.style.cursor = 'grab';
      container.style.userSelect = 'auto';
    }
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    const container = document.getElementById('table-container');
    if (container) {
      container.style.cursor = 'grab';
      container.style.userSelect = 'auto';
    }
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const container = document.getElementById('table-container');
    if (!container) return;
    
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 2; // Multiply for faster scroll
    container.scrollLeft = scrollLeft - walk;
  };

  // Use dynamic stages from system settings
  const stages = systemStages;

  // Use dynamic industries from system settings
  const industries = systemIndustries;

  const flags = [
    { id: 'hot', title: '🔥 Огонь', color: 'bg-orange-100 text-orange-800' },
    { id: 'risk', title: '⚠️ Риск срыва', color: 'bg-red-100 text-red-800' },
    { id: 'paused', title: '⏸️ Пауза', color: 'bg-gray-100 text-gray-800' },
    { id: 'waiting_construction', title: '🏗️ Ждем стройготовность', color: 'bg-blue-100 text-blue-800' },
    { id: 'waiting_funding', title: '💰 Ждут финансирования', color: 'bg-cyan-100 text-cyan-800' }
  ];

  useEffect(() => {
    loadProjects();
    loadManagers();
    
    // Restore filters from localStorage if returning from project detail
    const savedFilters = localStorage.getItem('projectsListFilters');
    if (savedFilters) {
      try {
        const restoredState = JSON.parse(savedFilters);
        
        // Safely restore filters with fallbacks
        const restoredFilters = {
          search: restoredState.filters?.search || '',
          industry: restoredState.filters?.industry || '',
          stages: Array.isArray(restoredState.filters?.stages) ? restoredState.filters.stages : [], // Ensure it's an array
          flag: restoredState.filters?.flag || '',
          manager: restoredState.filters?.manager || '',
          budgetMin: restoredState.filters?.budgetMin || '',
          budgetMax: restoredState.filters?.budgetMax || ''
        };
        
        setFilters(restoredFilters);
        setShowLostProjects(restoredState.showLostProjects || false);
        setSortBy(restoredState.sortBy || 'deadline');
        setSortOrder(restoredState.sortOrder || 'asc');
        
        // Clear the saved filters to avoid restoring on every page load
        localStorage.removeItem('projectsListFilters');
        
        console.log('Restored filters from navigation:', restoredFilters);
      } catch (error) {
        console.warn('Failed to restore filters:', error);
      }
    }
  }, []);

  useEffect(() => {
    applyFilters();
  }, [projects, filters, sortBy, sortOrder, showLostProjects]);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const response = await axios.get(`${API}/managers`);
      setManagers(response.data);
    } catch (error) {
      console.error('Failed to load managers:', error);
    }
  };

  const handleAssignManager = async (projectId, managerId) => {
    try {
      await axios.put(`${API}/projects/${projectId}/assign-manager`, { manager_id: managerId });
      setAssigningManager(null);
      loadProjects(); // Reload to see updated assignment
    } catch (error) {
      console.error('Failed to assign manager:', error);
      alert('Ошибка при назначении менеджера: ' + (error.response?.data?.detail || error.message));
    }
  };

  const applyFilters = () => {
    let filtered = [...projects];

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(project =>
        (project.name || '').toLowerCase().includes(filters.search.toLowerCase()) ||
        (project.city || '').toLowerCase().includes(filters.search.toLowerCase()) ||
        (project.customer_company || '').toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Industry filter
    if (filters.industry) {
      filtered = filtered.filter(project => project.industry === filters.industry);
    }

    // Stages filter (multiple selection)
    if (filters.stages && Array.isArray(filters.stages) && filters.stages.length > 0) {
      filtered = filtered.filter(project => filters.stages.includes(project.stage));
    }

    // Hide lost projects by default
    if (!showLostProjects) {
      filtered = filtered.filter(project => project.stage !== 'lost');
    }

    // Flag filter
    if (filters.flag) {
      filtered = filtered.filter(project => project.flags && project.flags.includes(filters.flag));
    }

    // Manager filter (if user is lead/admin)
    if (filters.manager && (user?.role === 'lead' || user?.role === 'admin')) {
      filtered = filtered.filter(project => 
        project.assigned_manager_id === filters.manager || 
        (filters.manager === 'unassigned' && !project.assigned_manager_id)
      );
    }

    // Budget range filter
    if (filters.budgetMin) {
      const minBudget = parseFloat(filters.budgetMin);
      filtered = filtered.filter(project => 
        project.budget_amount && project.budget_amount >= minBudget
      );
    }
    
    if (filters.budgetMax) {
      const maxBudget = parseFloat(filters.budgetMax);
      filtered = filtered.filter(project => 
        project.budget_amount && project.budget_amount <= maxBudget
      );
    }

    // Sorting
    if (sortBy === 'budget') {
      filtered.sort((a, b) => {
        const budgetA = a.budget_amount || 0;
        const budgetB = b.budget_amount || 0;
        return sortOrder === 'desc' ? budgetB - budgetA : budgetA - budgetB;
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();
        return sortOrder === 'desc' ? nameB.localeCompare(nameA) : nameA.localeCompare(nameB);
      });
    } else if (sortBy === 'created_at') {
      filtered.sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
      });
    } else if (sortBy === 'deadline') {
      // Sort by quarter + year (deadline), projects without deadline go to the end
      filtered.sort((a, b) => {
        // Create deadline scores for comparison
        const getDeadlineScore = (project) => {
          const quarter = project.quarter;
          const year = project.year || project.build_start_year;
          
          if (!quarter || !year) {
            return 9999; // Projects without deadline go to the end
          }
          
          // Convert quarter to numeric (Q1=1, Q2=2, etc.)
          let quarterNum = 1;
          if (typeof quarter === 'string') {
            const match = quarter.match(/(\d+)/);
            if (match) {
              quarterNum = parseInt(match[1]);
            }
          } else if (typeof quarter === 'number') {
            quarterNum = quarter;
          }
          
          // Create combined score: year * 10 + quarter
          return year * 10 + quarterNum;
        };
        
        const scoreA = getDeadlineScore(a);
        const scoreB = getDeadlineScore(b);
        
        return sortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB;
      });
    }

    setFilteredProjects(filtered);
  };

  const updateFilter = (key, value) => {
    setFilters({
      ...filters,
      [key]: value
    });
  };

  const toggleStageFilter = (stageId) => {
    const currentStages = Array.isArray(filters.stages) ? filters.stages : []; // Ensure it's an array
    const newStages = currentStages.includes(stageId)
      ? currentStages.filter(s => s !== stageId)  // Remove if already selected
      : [...currentStages, stageId];  // Add if not selected
    
    setFilters({
      ...filters,
      stages: newStages
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      industry: '',
      stages: [], // Reset stages array
      flag: '',
      manager: '',
      budgetMin: '',
      budgetMax: ''
    });
  };

  const formatCurrency = (amount, currency = 'KZT') => {
    if (!amount) return '-';
    
    if (currency === 'KZT') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else if (currency === 'USD') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₸';
    } else if (currency === 'RUB') {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ₽';
    } else {
      return new Intl.NumberFormat('ru-RU').format(amount) + ' ' + currency;
    }
  };

  const getLastTask = (project) => {
    const tasks = project.tasks || [];
    if (tasks.length === 0) return null;
    
    // Get the most recent task (by created date or just the last in array)
    const sortedTasks = tasks.sort((a, b) => {
      if (a.created_at && b.created_at) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });
    
    return sortedTasks[0];
  };

  const handleProjectCreated = (newProject) => {
    setProjects([...projects, newProject]);
    setShowCreateModal(false);
  };

  const handleImportComplete = () => {
    loadProjects();
    setShowImportModal(false);
  };

  const updateProjectNextAction = async (projectId, nextAction) => {
    try {
      await axios.put(`${API}/projects/${projectId}`, { notes: nextAction });
      loadProjects();
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот проект? Это действие нельзя отменить.')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}`);
      // Reload projects list
      loadProjects();
      alert('Проект успешно удален!');
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert('Не удалось удалить проект: ' + (error.response?.data?.detail || 'Неизвестная ошибка'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <NavigationBar />

      {/* Main Content */}
      <main className="w-full py-6 px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900" data-testid="projects-list-title">
              Список проектов
            </h1>
            <p className="text-gray-600 mt-2">
              Всего проектов: {filteredProjects.length} из {projects.length}
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white/80 backdrop-blur-lg rounded-xl p-4 border border-white/20 mb-6">
            {/* Row 1: Search, Industry, Stages, Manager */}
            <div className="grid grid-cols-12 gap-3 mb-3">
              {/* Search */}
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder="Поиск проектов..."
                  value={filters.search}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  className="form-input w-full text-sm py-1.5"
                  data-testid="search-input"
                />
              </div>

              {/* Industry Filter */}
              <div className="col-span-3">
                <select
                  value={filters.industry}
                  onChange={(e) => updateFilter('industry', e.target.value)}
                  className="form-select w-full text-sm py-1.5"
                  data-testid="industry-filter"
                >
                  <option value="">Все отрасли</option>
                  {industries.map(industry => (
                    <option key={industry.id} value={industry.id}>{industry.title}</option>
                  ))}
                </select>
              </div>

              {/* Stage Filter - Multiple Selection */}
              <div className="col-span-6">
                <div className="text-xs text-gray-600 mb-1">Этапы (можно выбрать несколько):</div>
                <div className="flex flex-wrap gap-1">
                  {stages.map(stage => (
                    <button
                      key={stage.id}
                      onClick={() => toggleStageFilter(stage.id)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                        Array.isArray(filters.stages) && filters.stages.includes(stage.id)
                          ? 'bg-blue-600 text-white border-2 border-blue-600' 
                          : 'bg-gray-100 text-gray-600 hover:bg-blue-50 hover:text-blue-600 border border-gray-300'
                      }`}
                      data-testid={`stage-toggle-${stage.id}`}
                    >
                      {stage.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Manager Filter - если есть право */}
              {(user?.role === 'lead' || user?.role === 'admin') && (
                <div className="col-span-2">
                  <select
                    value={filters.manager}
                    onChange={(e) => updateFilter('manager', e.target.value)}
                    className="form-select w-full text-sm py-1.5"
                    data-testid="manager-filter"
                  >
                    <option value="">Менеджеры</option>
                    <option value="unassigned">Неназнач.</option>
                    {managers.map(manager => (
                      <option key={manager.id} value={manager.id}>
                        {manager.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Row 2: Budget Range, Sorting, Flags, Clear */}
            <div className="flex items-end gap-3">
              {/* Budget Range */}
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Бюджет от (₸)</label>
                <input
                  type="number"
                  placeholder="Мин"
                  value={filters.budgetMin}
                  onChange={(e) => updateFilter('budgetMin', e.target.value)}
                  className="form-input w-full text-sm py-1.5"
                  data-testid="budget-min-filter"
                />
              </div>

              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Бюджет до (₸)</label>
                <input
                  type="number"
                  placeholder="Макс"
                  value={filters.budgetMax}
                  onChange={(e) => updateFilter('budgetMax', e.target.value)}
                  className="form-input w-full text-sm py-1.5"
                  data-testid="budget-max-filter"
                />
              </div>

              {/* Sorting */}
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Сортировка</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-select w-full text-sm py-1.5"
                  data-testid="sort-by-select"
                >
                  <option value="">По умолчанию</option>
                  <option value="deadline">По срокам реализации</option>
                  <option value="budget">По бюджету</option>
                  <option value="name">По названию</option>
                  <option value="created_at">По дате создания</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">Порядок</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="form-select w-full text-sm py-1.5"
                  data-testid="sort-order-select"
                >
                  <option value="desc">↓ Убыв.</option>
                  <option value="asc">↑ Возр.</option>
                </select>
              </div>

              {/* Flag Toggle Buttons */}
              <div className="flex gap-2">
                {flags.map(flag => (
                  <button
                    key={flag.id}
                    onClick={() => updateFilter('flag', filters.flag === flag.id ? '' : flag.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                      filters.flag === flag.id 
                        ? flag.color + ' border-2 border-current' 
                        : 'bg-gray-100 text-gray-600 hover:' + flag.color
                    }`}
                    data-testid={`flag-toggle-${flag.id}`}
                  >
                    {flag.title}
                  </button>
                ))}
                
                {/* Show Lost Projects Toggle */}
                <button
                  onClick={() => setShowLostProjects(!showLostProjects)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap border-2 ${
                    showLostProjects 
                      ? 'bg-red-100 text-red-800 border-red-600' 
                      : 'bg-gray-100 text-gray-600 border-gray-300 hover:bg-red-50 hover:text-red-600'
                  }`}
                  data-testid="show-lost-projects-toggle"
                  title={showLostProjects ? 'Скрыть проигранные проекты' : 'Показать проигранные проекты'}
                >
                  {showLostProjects ? '👁️ Скрыть проигранные' : '🙈 Показать проигранные'}
                </button>
              </div>

              {/* Clear Filters */}
              <button
                onClick={clearFilters}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-600 hover:bg-red-200 whitespace-nowrap"
                data-testid="clear-filters-button"
              >
                ✖ Очистить
              </button>
            </div>
          </div>
          
          {/* Projects Count Info */}
          <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm text-blue-800">
            <div className="flex justify-between items-center">
              <span>
                Показано: <strong>{filteredProjects.length}</strong> из <strong>{projects.filter(p => p.stage !== 'lost').length}</strong> проектов
              </span>
              {!showLostProjects && (
                <span className="text-blue-600">
                  🙈 Скрыто проигранных: <strong>{projects.filter(p => p.stage === 'lost').length}</strong>
                </span>
              )}
            </div>
          </div>

          {/* Projects Display - Table for Desktop, Cards for Mobile */}
          <div className="bg-white/80 backdrop-blur-lg rounded-xl border border-white/20 overflow-hidden">
            {/* Mobile Cards View - Hidden on larger screens */}
            <div className="block lg:hidden">
              {filteredProjects.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>Проекты не найдены</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredProjects.map((project) => (
                    <div key={project.id} className="p-4 hover:bg-gray-50">
                      {/* Mobile Project Card */}
                      <div className="space-y-3">
                        {/* Project Title and Company */}
                        <div>
                          <Link 
                            to={`/project/${project.id}`}
                            className="text-blue-600 font-medium hover:text-blue-800 block truncate"
                            onClick={() => {
                              // Save referrer info for smart back navigation
                              localStorage.setItem('projectDetailReferrer', JSON.stringify({
                                path: '/projects-list',
                                filters: filters,
                                showLostProjects: showLostProjects,
                                sortBy: sortBy,
                                sortOrder: sortOrder
                              }));
                            }}
                          >
                            {project.name}
                          </Link>
                          <p className="text-sm text-gray-600 truncate">{project.customer_company || 'Не указана'}</p>
                        </div>
                        
                        {/* Budget and Stage */}
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-lg font-semibold text-green-600">
                              {project.budget_amount ? `${project.budget_amount}₸` : 'Не указана'}
                            </span>
                          </div>
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                            {project.stage ? getStageLabel(project.stage) : 'Не указан'}
                          </span>
                        </div>
                        
                        {/* Flags */}
                        {project.flags && project.flags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {project.flags.map((flag) => (
                              <span
                                key={flag}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  flag === 'hot' ? 'bg-orange-100 text-orange-800' :
                                  flag === 'risk' ? 'bg-red-100 text-red-800' :
                                  flag === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                                  flag === 'waiting_construction' ? 'bg-blue-100 text-blue-800' :
                                  flag === 'waiting_funding' ? 'bg-cyan-100 text-cyan-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {flag === 'hot' ? '🔥 Горячий' :
                                 flag === 'risk' ? '⚠️ Риск' :
                                 flag === 'paused' ? '⏸️ Пауза' :
                                 flag === 'waiting_construction' ? '🏗️ Стройготовность' :
                                 flag === 'waiting_funding' ? '💰 Финансирование' :
                                 flag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Last Task / Next Action */}
                        {(() => {
                          const lastTask = getLastTask(project);
                          
                          if (lastTask) {
                            return (
                              <div className="text-sm border-t border-gray-200 pt-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600 font-medium">Последняя задача:</span>
                                  {lastTask.completed && (
                                    <span className="text-green-600 text-xs">✓ Выполнено</span>
                                  )}
                                </div>
                                <div className={`mt-1 ${lastTask.completed ? 'text-green-600 line-through' : 'text-gray-900'}`}>
                                  {lastTask.title}
                                </div>
                                {lastTask.deadline && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    До: {new Date(lastTask.deadline).toLocaleDateString('ru-RU')}
                                  </div>
                                )}
                              </div>
                            );
                          } else if (project.next_action) {
                            return (
                              <div className="text-sm border-t border-gray-200 pt-3">
                                <span className="text-gray-600 font-medium">Следующее действие:</span>
                                <div className="text-gray-900 mt-1 italic">
                                  {project.next_action}
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* Manager and Location */}
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>{project.assigned_manager_name || 'Не назначен'}</span>
                          <span>{project.city || 'Не указан'}</span>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setEditingProject(project);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Редактировать
                          </button>
                          
                          {(user?.role === 'admin' || user?.role === 'lead') && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                deleteProject(project.id);
                              }}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                              title="Удалить проект"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Table View - Hidden on mobile */}
            <div className="hidden lg:block projects-table-container">
              {/* Table Navigation Controls */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <div className="text-sm text-gray-600">
                Прокрутите таблицу горизонтально или используйте кнопки навигации
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const container = document.getElementById('table-container');
                    if (container) container.scrollLeft -= 300;
                  }}
                  className="p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border"
                  title="Прокрутить влево"
                  data-testid="table-scroll-left"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={() => {
                    const container = document.getElementById('table-container');
                    if (container) container.scrollLeft += 300;
                  }}
                  className="p-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border"
                  title="Прокрутить вправо"
                  data-testid="table-scroll-right"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Table Container with Enhanced Scroll */}
            <div 
              id="table-container"
              className="overflow-x-auto select-none"
              style={{ 
                scrollbarWidth: 'auto',
                height: 'calc(100vh - 400px)',
                minHeight: '400px'
              }}
              onMouseDown={handleMouseDown}
              onMouseLeave={handleMouseLeave}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              <table className="w-full divide-y divide-gray-200" style={{ minWidth: '2000px' }}>
                <thead className="bg-gray-50/50 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '220px' }}>
                      Проект
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '180px' }}>
                      Компания
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '130px' }}>
                      Сумма
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '130px' }}>
                      Город поставки
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>
                      Срок реализации
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '220px' }}>
                      Статус
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '220px' }}>
                      След. действие
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>
                      Менеджер
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '140px' }}>
                      Этап
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '120px' }}>
                      Тег
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ minWidth: '150px' }}>
                      Действия
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white/30 divide-y divide-gray-200">
                  {filteredProjects.map((project) => (
                    <tr
                      key={project.id}
                      className="hover:bg-gray-50/50 transition-colors"
                      data-testid={`project-row-${project.id}`}
                    >
                      {/* Проект */}
                      <td className="px-4 py-4" style={{ minWidth: '220px' }}>
                        <div className="flex items-start">
                          <div className="w-full">
                            <div className="text-sm font-medium text-gray-900 break-words leading-tight">
                              <Link 
                                to={`/project/${project.id}`}
                                className="project-link hover:text-blue-600 transition-colors"
                                data-testid={`project-link-${project.id}`}
                                onClick={() => {
                                  // Save referrer info for smart back navigation
                                  localStorage.setItem('projectDetailReferrer', JSON.stringify({
                                    path: '/projects-list',
                                    filters: filters,
                                    showLostProjects: showLostProjects,
                                    sortBy: sortBy,
                                    sortOrder: sortOrder
                                  }));
                                }}
                              >
                                {project.name}
                              </Link>
                            </div>
                            <div className="text-xs text-gray-500 mt-1 break-words">
                              {industries.find(i => i.id === project.industry)?.title}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Компания */}
                      <td className="px-4 py-4" style={{ minWidth: '180px' }}>
                        <div className="text-sm text-gray-900 break-words leading-tight">
                          {project.customer_company || 'Не указана'}
                        </div>
                      </td>

                      {/* Сумма */}
                      <td className="px-4 py-4" style={{ minWidth: '130px' }}>
                        <div className="text-sm font-medium text-gray-900 break-words">
                          {formatCurrency(project.budget_amount, project.budget_currency)}
                        </div>
                      </td>

                      {/* Город поставки */}
                      <td className="px-4 py-4" style={{ minWidth: '130px' }}>
                        <div className="text-sm text-gray-900 break-words">
                          {project.city}
                        </div>
                      </td>

                      {/* Срок реализации */}
                      <td className="px-4 py-4" style={{ minWidth: '140px' }}>
                        <div className="text-sm text-gray-900 break-words">
                          {project.quarter && project.build_start_year 
                            ? `${project.quarter} ${project.build_start_year}` 
                            : project.build_start_year || 'Не указан'
                          }
                        </div>
                      </td>

                      {/* Статус (последний комментарий) */}
                      <td className="px-4 py-4" style={{ minWidth: '220px' }}>
                        <div className="text-sm text-gray-900 break-words leading-tight">
                          {project.last_comment || project.notes || 'Нет комментариев'}
                        </div>
                        {project.last_comment_date && (
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(project.last_comment_date).toLocaleDateString('ru-RU')}
                          </div>
                        )}
                      </td>

                      {/* След. действие */}
                      <td className="px-4 py-4" style={{ minWidth: '220px' }}>
                        {(() => {
                          const lastTask = getLastTask(project);
                          
                          if (lastTask) {
                            return (
                              <div className="text-sm">
                                <div className={`font-medium ${lastTask.completed ? 'text-green-600 line-through' : 'text-gray-900'}`}>
                                  {lastTask.title}
                                </div>
                                {lastTask.deadline && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    До: {new Date(lastTask.deadline).toLocaleDateString('ru-RU')}
                                    {lastTask.completed && (
                                      <span className="ml-2 text-green-600">✓ Выполнено</span>
                                    )}
                                  </div>
                                )}
                                {!lastTask.deadline && (
                                  <div className="text-xs text-gray-400 mt-1">Без срока</div>
                                )}
                              </div>
                            );
                          } else {
                            // Fallback to next_action if no tasks
                            return project.next_action ? (
                              <div className="text-sm text-gray-600 italic">
                                {project.next_action}
                                {project.next_action_deadline && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    До: {new Date(project.next_action_deadline).toLocaleDateString('ru-RU')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">Задачи не назначены</span>
                            );
                          }
                        })()}
                      </td>

                      {/* Менеджер */}
                      <td className="px-4 py-4" style={{ minWidth: '150px' }}>
                        {(user?.role === 'admin' || user?.role === 'lead') ? (
                          assigningManager === project.id ? (
                            <div className="flex flex-col space-y-1">
                              <select
                                onChange={(e) => {
                                  if (e.target.value) {
                                    handleAssignManager(project.id, e.target.value);
                                  }
                                }}
                                className="text-xs border border-gray-300 rounded px-2 py-1"
                                defaultValue=""
                              >
                                <option value="">Выберите менеджера</option>
                                {managers.map(manager => (
                                  <option key={manager.id} value={manager.id}>
                                    {manager.name} ({manager.role})
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => setAssigningManager(null)}
                                className="text-xs text-gray-600 hover:text-gray-800"
                              >
                                Отмена
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-900 break-words">
                                {project.assigned_manager_name || project.manager_name || 'Не назначен'}
                              </div>
                              <button
                                onClick={() => setAssigningManager(project.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 ml-1"
                                title="Назначить менеджера"
                              >
                                ✏️
                              </button>
                            </div>
                          )
                        ) : (
                          <div className="text-sm text-gray-900 break-words">
                            {project.assigned_manager_name || project.manager_name || user?.name || 'Не назначен'}
                          </div>
                        )}
                      </td>

                      {/* Этап */}
                      <td className="px-4 py-4" style={{ minWidth: '140px' }}>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium status-badge status-${project.stage.replace('_', '-')} break-words`}>
                          {stages.find(s => s.id === project.stage)?.title}
                        </span>
                      </td>

                      {/* Тег */}
                      <td className="px-4 py-4" style={{ minWidth: '120px' }}>
                        <div className="flex gap-1 flex-wrap">
                          {project.flags?.map((flag) => {
                            const flagConfig = flags.find(f => f.id === flag);
                            return (
                              <span
                                key={flag}
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${flagConfig?.color || 'bg-gray-100 text-gray-800'} break-words`}
                              >
                                {flagConfig?.title || flag}
                              </span>
                            );
                          })}
                        </div>
                      </td>

                      {/* Действия */}
                      <td className="px-4 py-4" style={{ minWidth: '150px' }}>
                        <div className="flex gap-2 items-center">
                          <Link
                            to={`/project/${project.id}`}
                            className="text-blue-600 hover:text-blue-900 transition-colors text-sm font-medium whitespace-nowrap"
                            data-testid={`view-project-${project.id}`}
                            onClick={() => {
                              // Save referrer info for smart back navigation
                              localStorage.setItem('projectDetailReferrer', JSON.stringify({
                                path: '/projects-list',
                                filters: filters,
                                showLostProjects: showLostProjects,
                                sortBy: sortBy,
                                sortOrder: sortOrder
                              }));
                            }}
                          >
                            Подробнее
                          </Link>
                          <RoleGuard allowedRoles={['admin', 'lead']}>
                            <button
                              onClick={() => deleteProject(project.id)}
                              className="text-red-600 hover:text-red-900 transition-colors text-sm font-medium ml-2"
                              data-testid={`delete-project-${project.id}`}
                              title="Удалить проект"
                            >
                              🗑️
                            </button>
                          </RoleGuard>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredProjects.length === 0 && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M34 40h10v-4a6 6 0 00-10.712-3.714M34 40H14m20 0v-4a9.971 9.971 0 00-.712-3.714M14 40H4v-4a6 6 0 0110.713-3.714M14 40v-4c0-1.313.253-2.566.713-3.714m0 0A10.003 10.003 0 0124 26c4.21 0 7.813 2.602 9.288 6.286M30 14a6 6 0 11-12 0 6 6 0 0112 0zm12 6a4 4 0 11-8 0 4 4 0 018 0zm-28 0a4 4 0 11-8 0 4 4 0 018 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Проекты не найдены</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {projects.length === 0 ? 'Создайте первый проект или импортируйте из Excel.' : 'Попробуйте изменить фильтры.'}
                </p>
              </div>
            )}
            </div>
          </div>
        </div>
      </main>

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onProjectCreated={handleProjectCreated}
        />
      )}

      {/* Import Excel Modal */}
      {showImportModal && (
        <ImportExcelModal
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}

      {/* Duplicate Analysis Modal */}
      {showDuplicateAnalysis && (
        <DuplicateAnalysis
          onClose={() => setShowDuplicateAnalysis(false)}
        />
      )}
    </div>
  );
};

export default ProjectsList;